'use client'

import { CategoryFaq } from '@/services/dataTypes'
import { HelpCircle } from 'lucide-react'
import React, { useState } from 'react'

const C = { PRIMARY: '#F8F246', SECONDARY: '#2CBBC3', BROWN: '#8C5826', LIGHT: '#E1F1F2', BORDER: '#ffe0c2' }

interface FAQItem {
    question: string
    answer: string
}

const CategoryFAQs = ({ faqs, categoryName }: { faqs: FAQItem[] | any[]; categoryName?: string }) => {
    const [active, setActive] = useState(0)

    if (!faqs?.length) return null

    return (
        <section className="py-14 px-4" style={{ background: '#fffaf5' }}
            aria-labelledby="cat-faq-heading" itemScope itemType="https://schema.org/FAQPage">

            <div className="max-w-4xl mx-auto">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] mb-2 text-center"
                    style={{ color: C.SECONDARY }}>
                    {categoryName ? `${categoryName} · ` : ''}FAQ
                </p>

                <h2 id="cat-faq-heading" className="text-2xl font-black text-center mb-8"
                    style={{ color: C.BROWN }}>
                    Frequently Asked Questions
                </h2>

                {/* Pill tabs */}
                <div className="flex flex-wrap justify-center gap-2 mb-8">
                    {faqs.map((faq, i) => (
                        <button key={i} onClick={() => setActive(i)}
                            className="px-4 py-2 rounded-full text-xs font-semibold transition-all duration-200"
                            style={{
                                background: i === active ? C.BROWN : '#ffffff',
                                color: i === active ? C.PRIMARY : `${C.BROWN}80`,
                                border: `1px solid ${i === active ? C.BROWN : C.BORDER}`,
                                fontWeight: i === active ? 700 : 500,
                            }}
                            itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                            <span itemProp="name">{faq.question}</span>
                        </button>
                    ))}
                </div>

                {/* Answer panel */}
                <div key={active}
                    className="rounded-2xl px-6 py-5 flex gap-4 items-start overflow-y-auto"
                    style={{
                        background: '#ffffff',
                        border: `1px solid ${C.BORDER}`,
                        boxShadow: '0 4px 20px rgba(140,88,38,0.06)',
                        animation: 'faqIn 0.2s ease',
                        minHeight: 100,
                    }}
                    itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                    <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ background: C.BROWN }}>
                        <HelpCircle size={15} style={{ color: C.PRIMARY }} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-bold uppercase tracking-widest mb-1.5"
                            style={{ color: C.SECONDARY }}>Answer</p>
                        <div className="text-sm leading-relaxed" style={{ color: `${C.BROWN}90` }}
                            itemProp="text"
                            dangerouslySetInnerHTML={{ __html: faqs[active].answer }} />
                    </div>
                </div>

                {/* Dot indicators */}
                <div className="flex justify-center gap-1.5 mt-5">
                    {faqs.map((_, i) => (
                        <button key={i} onClick={() => setActive(i)}
                            className="rounded-full transition-all duration-300"
                            style={{
                                width: i === active ? 18 : 5,
                                height: 5,
                                background: i === active ? C.BROWN : `${C.BROWN}20`,
                            }}
                            aria-label={`Question ${i + 1}`} />
                    ))}
                </div>
            </div>

            <style dangerouslySetInnerHTML={{ __html: `
                @keyframes faqIn {
                    from { opacity: 0; transform: translateY(6px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
            ` }} />
        </section>
    )
}

export default CategoryFAQs
